import React, { useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import {
  DateInput,
  ErrorBox,
  Field,
  GhostButton,
  ModalShell,
  NumberInput,
  PrimaryButton,
  Select,
  TextArea,
  TextInput,
} from "../../../components/FormControls";
import {
  GENDER_OPTIONS,
  PRODUCT_OPTIONS,
  TYPE_OPTIONS,
} from "../../../types/promos";

type Props = {
  token: string | null;
  onClose: () => void;
  onApplied: () => void;
};

const numOrNull = (v: string): number | null =>
  v.trim() === "" ? null : Number(v);

export default function CreateCouponModal({ token, onClose, onApplied }: Props) {
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [description, setDescription] = useState("");
  const [product, setProduct] = useState("ALL");
  const [type, setType] = useState("FIXED_AMOUNT_MONTHLY");
  const [detail, setDetail] = useState("1");
  const [percentage, setPercentage] = useState("");
  const [startMonth, setStartMonth] = useState("");
  const [gender, setGender] = useState("B");
  const [minAge, setMinAge] = useState("18");
  const [maxAge, setMaxAge] = useState("74");
  const [minPrice, setMinPrice] = useState("50");
  const [maxPrice, setMaxPrice] = useState("10000");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [conditions, setConditions] = useState("");
  const [exceptions, setExceptions] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (submitting) return;
    if (!name.trim() || !couponCode.trim() || !description.trim()) {
      setError("Nombre, código y descripción son obligatorios");
      return;
    }
    if (!startDate || !endDate) {
      setError("Las fechas de inicio y fin son obligatorias");
      return;
    }
    const ok = await confirm({
      title: "Crear cupón",
      message: `Se creará el cupón "${couponCode.trim()}". ¿Continuar?`,
      confirmLabel: "Crear cupón",
      tone: "primary",
    });
    if (!ok) return;

    setSubmitting(true);
    setError(null);
    try {
      await request(`/api/admin/operations/coupons`, {
        method: "POST",
        token,
        body: {
          name: name.trim(),
          coupon_code: couponCode.trim(),
          description: description.trim(),
          conditions: conditions.trim(),
          exceptions: exceptions.trim() || null,
          product,
          type,
          gender,
          detail: numOrNull(detail),
          percentage: numOrNull(percentage),
          start_month: numOrNull(startMonth),
          min_age: numOrNull(minAge),
          max_age: numOrNull(maxAge),
          min_price: numOrNull(minPrice),
          max_price: numOrNull(maxPrice),
          usage_limit: numOrNull(usageLimit),
          start_date: startDate,
          end_date: endDate,
        },
      });
      onApplied();
    } catch (err) {
      setError(describeError(err, "No se pudo crear el cupón"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      title="Nuevo cupón"
      onClose={onClose}
      width={600}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={submitting}>
            Cancelar
          </GhostButton>
          <PrimaryButton onClick={submit} disabled={submitting}>
            {submitting ? "Creando…" : "Crear cupón"}
          </PrimaryButton>
        </>
      }
    >
      <Field label="Nombre">
        <TextInput value={name} onChange={setName} disabled={submitting} maxLength={54} />
      </Field>
      <Field label="Código de cupón">
        <TextInput value={couponCode} onChange={setCouponCode} disabled={submitting} />
      </Field>
      <Field label="Descripción">
        <TextArea value={description} onChange={setDescription} disabled={submitting} rows={2} />
      </Field>

      <Row>
        <Field label="Producto">
          <Select value={product} onChange={setProduct} options={PRODUCT_OPTIONS} disabled={submitting} />
        </Field>
        <Field label="Género">
          <Select value={gender} onChange={setGender} options={GENDER_OPTIONS} disabled={submitting} />
        </Field>
      </Row>

      <Field label="Tipo de descuento">
        <Select value={type} onChange={setType} options={TYPE_OPTIONS} disabled={submitting} />
      </Field>
      <Row>
        <Field label="Detalle (meses/cantidad)">
          <NumberInput value={detail} onChange={setDetail} disabled={submitting} min={1} max={12} />
        </Field>
        <Field label="Valor (% o importe)">
          <NumberInput value={percentage} onChange={setPercentage} disabled={submitting} min={1} />
        </Field>
        <Field label="Mes inicial" hint="Solo para meses iniciales">
          <NumberInput value={startMonth} onChange={setStartMonth} disabled={submitting} min={1} max={12} />
        </Field>
      </Row>

      <Row>
        <Field label="Edad mínima">
          <NumberInput value={minAge} onChange={setMinAge} disabled={submitting} min={18} max={74} />
        </Field>
        <Field label="Edad máxima">
          <NumberInput value={maxAge} onChange={setMaxAge} disabled={submitting} min={18} max={74} />
        </Field>
      </Row>
      <Row>
        <Field label="Precio anual mínimo">
          <NumberInput value={minPrice} onChange={setMinPrice} disabled={submitting} min={50} />
        </Field>
        <Field label="Precio anual máximo">
          <NumberInput value={maxPrice} onChange={setMaxPrice} disabled={submitting} min={50} />
        </Field>
      </Row>

      <Row>
        <Field label="Fecha inicio">
          <DateInput value={startDate} onChange={setStartDate} disabled={submitting} />
        </Field>
        <Field label="Fecha fin">
          <DateInput value={endDate} onChange={setEndDate} disabled={submitting} />
        </Field>
      </Row>

      <Field label="Límite de usos" hint="Vacío = ilimitado">
        <NumberInput value={usageLimit} onChange={setUsageLimit} disabled={submitting} min={1} />
      </Field>
      <Field label="Condiciones generales">
        <TextArea value={conditions} onChange={setConditions} disabled={submitting} rows={3} />
      </Field>
      <Field label="Excepciones (opcional)">
        <TextArea value={exceptions} onChange={setExceptions} disabled={submitting} rows={2} />
      </Field>

      {error && <ErrorBox>{error}</ErrorBox>}
    </ModalShell>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${React.Children.count(children)}, 1fr)`,
        gap: "0.7rem",
      }}
    >
      {children}
    </div>
  );
}

export function describeError(err: unknown, fallback: string): string {
  if (err instanceof HttpError) {
    const body = err.body as { message?: string; error?: string } | undefined;
    const detail = (body?.message ?? body?.error ?? "").slice(0, 240);
    return `HTTP ${err.status}: ${detail || fallback}`;
  }
  return fallback;
}
